import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { authService } from '../services/dotnetApiService';
import { getUserPrivilegeLevel, getUserRoleInfo } from '../utils/roleUtils';

// Types
interface User {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    profile_picture?: string; // Keep this for backward compatibility, but map to profilePictureUrl
    profilePictureUrl?: string;
    user_type?: string;
    roles?: string[];
    is_active?: boolean;
    is_staff?: boolean;
    is_superuser?: boolean;
    date_joined?: string;
    last_login?: string;
    groups?: string[];
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    register: (userData: RegisterData) => Promise<void>;
    updateProfile: (userData: Partial<User>) => Promise<void>;
    backendType: string;

    // Role-based access helpers
    hasPrivilegeLevel: (level: number) => boolean;
    canPerformAction: (action: string) => boolean;
    getUserRole: () => { name: string; description: string; level: number };
}

interface RegisterData {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    password_confirm: string;
    user_type?: string;
    phone_number?: string;
}

interface AuthResponse {
    user: User;
    tokens: {
        access: string;
        refresh: string;
    };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Load user from localStorage on mount and check token expiration
    useEffect(() => {
        const validateAndLoadUser = () => {
            const storedUser = localStorage.getItem('user');
            const token = localStorage.getItem('accessToken');

            if (storedUser && token) {
                try {
                    // Check if token is expired (JWT tokens have 'exp' claim)
                    try {
                        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
                        const currentTime = Math.floor(Date.now() / 1000);
                        
                        // Add 60 seconds buffer to account for clock skew
                        if (tokenPayload.exp && tokenPayload.exp < (currentTime + 60)) {
                            console.log('Token expired, logging out');
                            logout();
                            setLoading(false);
                            return;
                        }
                    } catch (tokenParseError) {
                        // If we can't parse the token, assume it's invalid but don't logout immediately
                        // Let the API calls handle the validation
                        console.warn('Could not parse JWT token, but proceeding:', tokenParseError);
                    }

                    // Load user data from localStorage
                    const userData = JSON.parse(storedUser);
                    setUser(userData);
                    console.log('User loaded from localStorage:', userData.username);
                } catch (error) {
                    console.error('Failed to parse stored user data:', error);
                    // Clear invalid data
                    logout();
                }
            }

            setLoading(false);
        };

        validateAndLoadUser();
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        try {
            const response = await authService.login(email, password);

            const { user: userData, tokens } = response;

            // Store tokens and user data
            localStorage.setItem('accessToken', tokens.access);
            localStorage.setItem('refreshToken', tokens.refresh);
            localStorage.setItem('user', JSON.stringify(userData));

            setUser(userData);
        } catch (error: any) {
            // console.error('Login error details:', {
            //     message: error.message,
            //     response: error.response?.data,
            //     status: error.response?.status,
            //     backend: '.NET Core'
            // });

            // Provide more specific error messages
            let errorMessage = 'Login failed';
            if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
                errorMessage = 'Cannot connect to .NET authentication service. Please ensure it is running on port 5001.';
            } else if (error.response?.status === 401) {
                errorMessage = 'Invalid email or password';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            throw new Error(errorMessage);
        }
    };

    const register = async (userData: RegisterData): Promise<void> => {
        try {
            if (userData.password !== userData.password_confirm) {
                throw new Error('Passwords do not match');
            }

            // Transform data for .NET API
            const dotNetUserData = {
                email: userData.email,
                password: userData.password,
                firstName: userData.first_name,
                lastName: userData.last_name,
            };

            await authService.register(dotNetUserData);

            // Auto-login after successful registration
            const response = await authService.login(userData.email, userData.password);

            const { user: newUser, tokens } = response;

            // Store tokens and user data
            localStorage.setItem('accessToken', tokens.access);
            localStorage.setItem('refreshToken', tokens.refresh);
            localStorage.setItem('user', JSON.stringify(newUser));

            setUser(newUser);
        } catch (error: any) {
            console.error('Registration error:', error);
            throw error;
        }
    };

    const updateProfile = async (userData: Partial<User>): Promise<void> => {
        if (!user) {
            throw new Error('User not logged in');
        }

        try {
            // For now, just update local storage and state
            // In the future, this should call a profile update endpoint
            const updatedUser = { ...user, ...userData };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);
        } catch (error: any) {
            console.error('Profile update error:', error);
            throw error;
        }
    };

    const logout = (): void => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setUser(null);
    };

    // Role-based access control helpers
    const hasPrivilegeLevel = (requiredLevel: number): boolean => {
        if (!user) return false;
        return getUserPrivilegeLevel(user) >= requiredLevel;
    };

    const canPerformAction = (action: string): boolean => {
        if (!user) return false;

        // For now, implement basic role-based checks
        const userRole = getUserRole();

        switch (action) {
            case 'create_user':
            case 'delete_user':
                return userRole.level >= 4; // Admin level
            case 'manage_staff':
                return userRole.level >= 3; // Manager level
            case 'view_reports':
                return userRole.level >= 2; // Supervisor level
            case 'clock_in_out':
                return userRole.level >= 1; // Staff level
            default:
                return true; // Default allow for unknown actions
        }
    };

    const getUserRole = () => {
        if (!user) {
            return { name: 'None', description: 'Not logged in', level: 0 };
        }
        return getUserRoleInfo(user);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                login,
                logout,
                register,
                updateProfile,
                backendType: '.NET Core',
                hasPrivilegeLevel,
                canPerformAction,
                getUserRole,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
